/**
 * OCTOPUS Dashboard - FASE 3: Widget Library
 * WidgetLibraryPage - Galeria de templates de widgets
 * 
 * Estilo: Marítimo/Industrial moderno, tema dark
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LayoutGrid, 
  Filter,
  Search,
  Lock,
  Gauge,
  Hash,
  Type,
  Compass,
  MapPin,
  ToggleRight,
  LineChart,
  BarChart3,
  RefreshCw,
  Info,
} from 'lucide-react';

import { 
  GaugeRadial,
  GaugeLinear,
  GaugeRudder,
  NumericDisplay,
  TextDisplay,
  CompassRose,
  PositionDisplay,
  BooleanIndicator,
  DataChart,
} from '../components/widgets';

const API = process.env.REACT_APP_BACKEND_URL;

// Mapeamento de tipo para componente
const WIDGET_COMPONENTS = {
  GAUGE_RADIAL: GaugeRadial,
  GAUGE_LINEAR: GaugeLinear,
  GAUGE_RUDDER: GaugeRudder,
  NUMERIC: NumericDisplay,
  TEXT: TextDisplay,
  COMPASS: CompassRose,
  POSITION: PositionDisplay,
  BOOLEAN: BooleanIndicator,
  DATA_CHART: DataChart,
};

// Ícones por tipo
const TYPE_ICONS = {
  GAUGE_RADIAL: Gauge,
  GAUGE_LINEAR: BarChart3,
  GAUGE_RUDDER: BarChart3,
  NUMERIC: Hash,
  TEXT: Type,
  COMPASS: Compass,
  POSITION: MapPin,
  BOOLEAN: ToggleRight,
  DATA_CHART: LineChart,
};

// Labels de tipos
const TYPE_LABELS = {
  GAUGE_RADIAL: 'Medidor Radial',
  GAUGE_LINEAR: 'Medidor Linear',
  GAUGE_RUDDER: 'Leme (Rudder)',
  NUMERIC: 'Numérico',
  TEXT: 'Texto',
  COMPASS: 'Compasso',
  POSITION: 'Posição GPS',
  BOOLEAN: 'Indicador',
  DATA_CHART: 'Gráfico',
};

// Labels de categorias
const CATEGORY_LABELS = {
  propulsion: 'Propulsão',
  navigation: 'Navegação',
  environment: 'Ambiente',
  electrical: 'Elétrica',
  sensors: 'Sensores',
  steering: 'Governo',
  tanks: 'Tanques',
  universal: 'Universal',
};

const WidgetLibraryPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [availableTypes, setAvailableTypes] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedType) params.append('type', selectedType);
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await axios.get(`${API}/api/widget-templates?${params.toString()}`, {
        headers: { Authorization: 'Bearer dev-mode-token-not-for-production' }
      });
      
      setTemplates(response.data.templates || []);
      setAvailableTypes(response.data.types || []);
      setAvailableCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  }, [selectedType, selectedCategory]);

  // Carregar templates
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Seed de templates
  const seedTemplates = async () => {
    try {
      await axios.post(`${API}/api/widget-templates/seed`, {}, {
        headers: { Authorization: 'Bearer dev-mode-token-not-for-production' }
      });
      toast.success('Templates de sistema carregados');
      fetchTemplates();
    } catch (error) {
      console.error('Error seeding templates:', error);
      toast.error('Erro ao carregar templates de sistema');
    }
  };

  // Filtrar templates por busca
  const filteredTemplates = useMemo(() => {
    if (!searchTerm) return templates;
    const term = searchTerm.toLowerCase();
    return templates.filter(t => 
      t.name.toLowerCase().includes(term) ||
      t.description?.toLowerCase().includes(term)
    );
  }, [templates, searchTerm]);

  // Agrupar por categoria
  const groupedTemplates = useMemo(() => {
    const groups = {};
    filteredTemplates.forEach(template => {
      const categories = template.compatible_categories || ['universal'];
      categories.forEach(cat => {
        if (!groups[cat]) groups[cat] = [];
        // Evitar duplicatas
        if (!groups[cat].find(t => t.id === template.id)) {
          groups[cat].push(template);
        }
      });
    });
    return groups;
  }, [filteredTemplates]);

  // Renderizar preview do widget
  const renderWidgetPreview = (template, size = 'normal') => {
    const Component = WIDGET_COMPONENTS[template.type];
    if (!Component) return null;

    const config = template.default_config || {};
    
    // Dados de preview baseados no tipo
    const previewProps = getPreviewProps(template);
    
    // Ajustar tamanho baseado no componente
    const sizeMultiplier = size === 'large' ? 1.2 : (size === 'small' ? 0.7 : 1);
    
    if (template.type === 'GAUGE_RADIAL' || template.type === 'COMPASS') {
      return <Component {...previewProps} size={160 * sizeMultiplier} />;
    }
    
    if (template.type === 'GAUGE_LINEAR') {
      return <Component {...previewProps} width={200 * sizeMultiplier} height={60} />;
    }
    
    if (template.type === 'GAUGE_RUDDER') {
      return <Component {...previewProps} width={220 * sizeMultiplier} height={80} />;
    }
    
    if (template.type === 'DATA_CHART') {
      return <Component {...previewProps} width={300 * sizeMultiplier} height={150} />;
    }
    
    if (template.type === 'POSITION') {
      return <Component {...previewProps} />;
    }
    
    return <Component {...previewProps} size="medium" />;
  };

  // Gerar props de preview baseado no template
  const getPreviewProps = (template) => {
    const config = template.default_config || {};
    const baseProps = {
      label: template.name,
      showLabel: config.show_label !== false,
      showUnit: config.show_unit !== false,
      decimalPlaces: config.decimal_places ?? 1,
      zones: config.zones?.map(z => ({
        min_value: z.min_value,
        max_value: z.max_value,
        color: z.color,
        label: z.label,
      })),
    };

    // Props específicos por tipo
    switch (template.type) {
      case 'GAUGE_RADIAL':
        return {
          ...baseProps,
          value: config.scale_min + (config.scale_max - config.scale_min) * 0.6,
          min: config.scale_min ?? 0,
          max: config.scale_max ?? 100,
          unit: getUnitForTemplate(template),
        };
      
      case 'GAUGE_LINEAR':
        return {
          ...baseProps,
          value: config.scale_min + (config.scale_max - config.scale_min) * 0.65,
          min: config.scale_min ?? 0,
          max: config.scale_max ?? 100,
          unit: getUnitForTemplate(template),
        };
      
      case 'GAUGE_RUDDER':
        return {
          ...baseProps,
          value: 15, // Preview com leme 15° para estibordo
          min: config.scale_min ?? -40,
          max: config.scale_max ?? 40,
          unit: '°',
          centerZone: config.center_zone ?? 1,
          extremeZone: config.extreme_zone ?? 5,
        };
      
      case 'NUMERIC':
        return {
          ...baseProps,
          value: getNumericPreviewValue(template),
          unit: getUnitForTemplate(template),
        };
      
      case 'TEXT':
        return {
          ...baseProps,
          value: 'ATIVO',
          color: '#22c55e',
        };
      
      case 'COMPASS':
        return {
          ...baseProps,
          value: 135,
          showDegrees: true,
        };
      
      case 'POSITION':
        return {
          ...baseProps,
          value: { latitude: -28.12345, longitude: -48.65432 },
        };
      
      case 'BOOLEAN':
        return {
          ...baseProps,
          value: true,
          variant: 'status',
        };
      
      case 'DATA_CHART':
        return {
          ...baseProps,
          value: [],
          unit: '',
          color: '#22c55e',
        };
      
      default:
        return baseProps;
    }
  };

  const getUnitForTemplate = (template) => {
    const unitMap = {
      'tpl-tachometer': 'rpm',
      'tpl-engine-temp': '°C',
      'tpl-oil-pressure': 'bar',
      'tpl-fuel-consumption': 'L/h',
      'tpl-engine-hours': 'h',
      'tpl-speedometer-sog': 'kn',
      'tpl-ambient-temp': '°C',
      'tpl-barometer': 'hPa',
      'tpl-depth': 'm',
      'tpl-wind-speed': 'kn',
      'tpl-voltmeter-12v': 'V',
      'tpl-voltmeter-24v': 'V',
      'tpl-voltmeter-48v': 'V',
      'tpl-ammeter': 'A',
      'tpl-soc': '%',
      'tpl-tank-level': '%',
      'tpl-rudder-angle': '°',
      'tpl-xte': 'm',
    };
    return unitMap[template.id] || '';
  };

  const getNumericPreviewValue = (template) => {
    const valueMap = {
      'tpl-fuel-consumption': 45.5,
      'tpl-engine-hours': 1234.5,
      'tpl-ambient-temp': 25.3,
      'tpl-depth': 18.5,
      'tpl-numeric-generic': 42.0,
    };
    return valueMap[template.id] || 42.0;
  };

  return (
    <div className="page-container space-y-6" data-testid="widget-library-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <LayoutGrid className="w-8 h-8 text-primary shrink-0" />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-foreground">Biblioteca de Widgets</h1>
            <p className="text-muted-foreground text-sm">
              FASE 3 - Templates visuais para dashboards
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={seedTemplates}
            className="flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors"
            data-testid="seed-templates-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Carregar Templates
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-card rounded-lg">
        {/* Busca */}
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar widgets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none focus:border-primary"
            data-testid="search-input"
          />
        </div>

        {/* Filtro por tipo */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none focus:border-primary"
            data-testid="type-filter"
          >
            <option value="">Todos os tipos</option>
            {availableTypes.map(type => (
              <option key={type} value={type}>
                {TYPE_LABELS[type] || type}
              </option>
            ))}
          </select>
        </div>

        {/* Filtro por categoria */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none focus:border-primary"
          data-testid="category-filter"
        >
          <option value="">Todas as categorias</option>
          {availableCategories.map(cat => (
            <option key={cat} value={cat}>
              {CATEGORY_LABELS[cat] || cat}
            </option>
          ))}
        </select>

        {/* Contador */}
        <div className="text-muted-foreground text-sm">
          {filteredTemplates.length} templates
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Grid de Templates */}
      {!loading && filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <LayoutGrid className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">
            Nenhum template encontrado
          </h3>
          <p className="text-muted-foreground text-sm mb-4">
            Clique em "Carregar Templates" para popular a biblioteca
          </p>
          <button
            onClick={seedTemplates}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
          >
            Carregar Templates de Sistema
          </button>
        </div>
      )}

      {/* Templates agrupados por categoria */}
      {!loading && Object.keys(groupedTemplates).length > 0 && (
        <div className="space-y-8">
          {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
            <div key={category} className="space-y-4">
              {/* Header da categoria */}
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-foreground">
                  {CATEGORY_LABELS[category] || category}
                </h2>
                <span className="text-muted-foreground text-sm">
                  ({categoryTemplates.length})
                </span>
              </div>

              {/* Grid de widgets */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {categoryTemplates.map(template => {
                  const TypeIcon = TYPE_ICONS[template.type] || Gauge;
                  
                  return (
                    <div
                      key={template.id}
                      className={`
                        relative p-4 bg-card rounded-xl border border-border
                        hover:border-primary transition-all cursor-pointer
                        ${selectedTemplate?.id === template.id ? 'ring-2 ring-primary' : ''}
                      `}
                      onClick={() => setSelectedTemplate(template)}
                      data-testid={`template-card-${template.id}`}
                    >
                      {/* Badge de sistema */}
                      {template.is_system && (
                        <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-secondary rounded text-xs text-muted-foreground">
                          <Lock className="w-3 h-3" />
                          Sistema
                        </div>
                      )}

                      {/* Preview do widget */}
                      <div className="flex items-center justify-center min-h-[160px] mb-4">
                        {renderWidgetPreview(template)}
                      </div>

                      {/* Info do template */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <TypeIcon className="w-4 h-4 text-primary shrink-0" />
                          <h3 className="font-medium text-foreground truncate">
                            {template.name}
                          </h3>
                        </div>

                        {template.description && (
                          <p className="text-muted-foreground text-xs line-clamp-2">
                            {template.description}
                          </p>
                        )}

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1">
                          <span className="px-2 py-0.5 bg-secondary rounded text-xs text-secondary-foreground">
                            {TYPE_LABELS[template.type] || template.type}
                          </span>
                          {template.compatible_data_types?.map(dt => (
                            <span
                              key={dt}
                              className="px-2 py-0.5 bg-primary/10 rounded text-xs text-primary"
                            >
                              {dt}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de detalhes do template */}
      {selectedTemplate && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedTemplate(null)}
        >
          <div
            className="bg-card rounded-xl border border-border p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                  {selectedTemplate.name}
                  {selectedTemplate.is_system && (
                    <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                  )}
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  {selectedTemplate.description}
                </p>
              </div>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="text-muted-foreground hover:text-foreground p-1 shrink-0"
              >
                ✕
              </button>
            </div>

            {/* Preview grande */}
            <div className="flex items-center justify-center p-6 bg-background rounded-lg mb-6">
              {renderWidgetPreview(selectedTemplate, 'large')}
            </div>

            {/* Detalhes técnicos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Tipo:</span>
                <span className="ml-2 text-foreground">
                  {TYPE_LABELS[selectedTemplate.type]}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Componente:</span>
                <span className="ml-2 text-foreground font-mono">
                  {selectedTemplate.component_name}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Dados aceitos:</span>
                <span className="ml-2 text-foreground">
                  {selectedTemplate.compatible_data_types?.join(', ')}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Categorias:</span>
                <span className="ml-2 text-foreground">
                  {selectedTemplate.compatible_categories?.map(c => CATEGORY_LABELS[c] || c).join(', ')}
                </span>
              </div>
            </div>

            {/* Config padrão */}
            {selectedTemplate.default_config && (
              <div className="mt-6">
                <h3 className="text-foreground font-medium mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-primary" />
                  Configuração Padrão
                </h3>
                <div className="bg-background rounded-lg p-4 font-mono text-xs text-muted-foreground overflow-x-auto">
                  <pre>{JSON.stringify(selectedTemplate.default_config, null, 2)}</pre>
                </div>
              </div>
            )}

            {/* Aviso de template de sistema */}
            {selectedTemplate.is_system && (
              <div className="mt-4 flex items-center gap-2 p-3 bg-yellow-900/20 border border-yellow-700/50 rounded-lg text-yellow-400 text-sm">
                <Lock className="w-4 h-4 flex-shrink-0" />
                Este é um template de sistema e não pode ser editado ou removido.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetLibraryPage;
